import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import APIURL from '../../common/Config';
import "../blog/BlogList.css"; 
import Header from "../header/header";
import Footer from "../footer/footer";

const slugify = (text) =>
    (text || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

const formatScheduleDate = (numDate) => {
    if (!numDate) return "Invalid date";
    const str = String(numDate);
    if (str.length !== 8) return "Invalid date";
    const year = str.slice(0, 4);
    const month = str.slice(4, 6);
    const day = str.slice(6, 8);
    return `${day}-${month}-${year}`;
};

const BlogDetail = () => {
    const { slug } = useParams();
    const [blogContent, setBlogContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [seoMetaTags, setSeoMetaTags] = useState([]);
    const [documentTitle, setDocumentTitle] = useState("");

    const parseMetaTags = (metaTagsArray) => {
        const parsedTags = [];
        let titleFromMeta = "";

        metaTagsArray.forEach((tagString) => {
            if (tagString.includes("<title>")) {
                const match = tagString.match(/<title>(.*?)<\/title>/);
                if (match && match[1]) {
                    titleFromMeta = match[1];
                }
            } else if (tagString.includes("<meta")) {
                const nameMatch = tagString.match(/name="([^"]*)"/);
                const propertyMatch = tagString.match(/property="([^"]*)"/);
                const contentMatch = tagString.match(/content="([^"]*)"/);

                const name = nameMatch ? nameMatch[1] : null;
                const property = propertyMatch ? propertyMatch[1] : null;
                const content = contentMatch ? contentMatch[1] : "";

                if (name || property) {
                    parsedTags.push({
                        type: "meta",
                        name: name,
                        property: property,
                        content: content,
                    });
                }
            }
        });
        return { title: titleFromMeta, metaTags: parsedTags };
    };

    const fetchBlogBySlug = async () => {
        setLoading(true);
        setError(null);

        const cacheKey = `blogDetail-${slug}`;

        try {
            const cachedItem = localStorage.getItem(cacheKey);
            if (cachedItem) {
                const { data, timestamp, seoMetaTags: cachedSeoMetaTags, documentTitle: cachedDocumentTitle } = JSON.parse(cachedItem);
                const now = new Date().getTime();
                const cacheDuration = 30 * 60 * 1000; 

                if (now - timestamp < cacheDuration) {
                    console.log("Using cached blog detail for:", slug);
                    setBlogContent(data);
                    setSeoMetaTags(cachedSeoMetaTags);
                    setDocumentTitle(cachedDocumentTitle);
                    setLoading(false);
                    return;
                } else {
                    console.log("Cached blog detail expired for:", slug);
                    localStorage.removeItem(cacheKey);
                }
            }
        } catch (e) {
            console.error("Error reading from localStorage cache for blog detail:", e);
            localStorage.removeItem(cacheKey);
        }

       
        let retries = 0;
        const MAX_DELAY_MS = 60 * 1000; 
        const INITIAL_DELAY_MS = 1000; 

        while (true) { 
            try {
               
                const todayFormatted = new Date().toISOString().split("T")[0].replace(/-/g, "");
                const listResponse = await axios.get(`${APIURL}/astroblogs/by-date?startDate=20250101&endDate=${todayFormatted}`);

                if (listResponse.status !== 200) {
                    throw new Error(`Failed to fetch blog list: Status ${listResponse.status}`);
                }

                const blogList = listResponse.data;
                const matchedBlog = blogList.find(
                    (blog) => slugify(blog.heading) === slug
                );

                if (!matchedBlog) {
                    
                    setError("Blog not found. Please check the URL.");
                    setLoading(false);
                    return; 
                }

                
                const blogResponse = await axios.get(`${APIURL}/astroblogs/getBlogWithImages/${matchedBlog.id}`);

                if (blogResponse.status !== 200) {
                    throw new Error(`Failed to fetch blog content: Status ${blogResponse.status}`);
                }

                const { blog, images } = blogResponse.data;

                const imageMap = {};
                images.forEach(img => {
                    imageMap[decodeURIComponent(img.contentKey.trim())] = img.contentUrl;
                });

                let processedHtml = blog.htmlContent || ""; 
                processedHtml = processedHtml.replace(/<img[^>]+src="([^">]+)"[^>]*>/gi, (match, srcValue) => {
                    const fileName = decodeURIComponent(srcValue.trim().split('/').pop());
                    const newUrl = imageMap[fileName];
                    if (newUrl) {
                        return match.replace(/src="[^"]*"/, `src="${newUrl}"`);
                    }
                    return match;
                });

                const contentToCache = {
                    ...blog,
                    htmlContent: processedHtml
                };

                let currentParsedMetaTags = [];
                let currentDocumentTitle = blog.mainHeading;

                if (blog.metaTags && blog.metaTags.length > 0) {
                    const { title, metaTags } = parseMetaTags(blog.metaTags);
                    if (title) {
                        currentDocumentTitle = title;
                    }
                    currentParsedMetaTags = metaTags;
                }

                const defaultMetaTags = [
                    { name: "description", content: blog.mainDescription || "Default description for blog post" },
                    { name: "keywords", content: blog.heading || "default, keywords, blog" },
                    { name: "author", content: blog.article || "Heavenly Expectation Private Limited" },
                    { name: "revisit-after", content: "30 days" },
                    { name: "distribution", content: "global" },
                    { name: "rating", content: "general" },
                ];

                defaultMetaTags.forEach(defaultTag => {
                    const isAlreadyPresent = currentParsedMetaTags.some(
                        tag => (tag.name === defaultTag.name && defaultTag.name) || (tag.property === defaultTag.property && defaultTag.property)
                    );
                    if (!isAlreadyPresent) {
                        currentParsedMetaTags.push(defaultTag);
                    }
                });

                setBlogContent(contentToCache);
                setSeoMetaTags(currentParsedMetaTags);
                setDocumentTitle(currentDocumentTitle);

                localStorage.setItem(cacheKey, JSON.stringify({
                    data: contentToCache,
                    timestamp: new Date().getTime(),
                    seoMetaTags: currentParsedMetaTags,
                    documentTitle: currentDocumentTitle,
                }));

                setLoading(false);
                return; 
            } catch (err) {
                console.error("Error fetching blog by slug:", err);
                
                if (err.message.includes("Blog not found")) {
                    setError(err.message);
                    setLoading(false);
                    return; 
                }

                setError("Failed to load blog content. Retrying..."); 
                retries++;
                const delay = Math.min(INITIAL_DELAY_MS * Math.pow(2, retries - 1), MAX_DELAY_MS);
                console.warn(`Attempt ${retries} failed. Retrying in ${delay / 1000}s...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
       
    };

    useEffect(() => {
        fetchBlogBySlug();
    }, [slug]);

    useEffect(() => {
        if (!loading && blogContent) {
            document.title = documentTitle;

            const setOrCreateMetaTag = (tagData) => {
                let metaTag;
                if (tagData.name) {
                    metaTag = document.querySelector(`meta[name="${tagData.name}"]`);
                } else if (tagData.property) {
                    metaTag = document.querySelector(`meta[property="${tagData.property}"]`);
                }

                if (metaTag) {
                    metaTag.setAttribute("content", tagData.content);
                } else {
                    metaTag = document.createElement('meta');
                    if (tagData.name) {
                        metaTag.setAttribute("name", tagData.name);
                    } else if (tagData.property) {
                        metaTag.setAttribute("property", tagData.property);
                    }
                    metaTag.setAttribute("content", tagData.content);
                    document.head.appendChild(metaTag);
                }
            };

            
            const existingMetaTags = document.head.querySelectorAll('meta[name], meta[property]');
            existingMetaTags.forEach(tag => {
                const name = tag.getAttribute('name');
                const property = tag.getAttribute('property');

               
                if (name && ['description', 'keywords', 'author', 'revisit-after', 'distribution', 'rating'].includes(name)) {
                    tag.remove();
                } else if (property && seoMetaTags.some(t => t.property === property)) {
                    tag.remove();
                }
            });

            seoMetaTags.forEach(tag => {
                setOrCreateMetaTag(tag);
            });
        }
    }, [loading, blogContent, seoMetaTags, documentTitle]);

    if (loading) {
        return (
            <div className="loader-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
                <div className="spinner" style={{ border: '4px solid rgba(0, 0, 0, 0.1)', borderTop: '4px solid #3498db', borderRadius: '50%', width: '50px', height: '50px', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '10px', fontSize: '1.2em', color: '#555' }}>Loading blog content...</p>
                <style>
                    {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                    `}
                </style>
            </div>
        );
    }

    if (error) return <p style={{ textAlign: 'center', color: 'red', marginTop: '20px' }}>Error: {error}</p>; // Display the specific error message
    if (!blogContent) return <p style={{ textAlign: 'center', color: '#555', marginTop: '20px' }}>Blog content not available.</p>;

    const {
        mainHeading = "No Title",
        mainImageUrl = "",
        scheduleDate,
        article = "Unknown",
        htmlContent = "",
    } = blogContent;

    return (
        <div>
            <Header />
            <div className="blogdetail">
                <h2>{mainHeading}</h2>
                <p className="blog-meta-info">
                    Published on {scheduleDate ? formatScheduleDate(scheduleDate) : "Unknown date"} by <span className="blog-author">{article}</span>
                </p>
                <div className="blog-content-html" dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </div>
            <Footer />
        </div>
    );
};

export default BlogDetail;
